import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_URL } from "../../config";

const VisitorsTable = () => {
  const [visitors, setVisitors] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState({ show: false, message: "", type: "" });

  const rowsPerPage = 10;
  const token = localStorage.getItem("token");

  const showAlert = (message, type) => {
    setAlert({ show: true, message, type });
    setTimeout(() => setAlert({ show: false, message: "", type: "" }), 3000);
  };

  const fetchVisitors = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}getAlldata`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });
      if (response.data.status === "success") {
        setVisitors(response.data.data);
      } else {
        showAlert("Failed to fetch visitors data", "error");
      }
    } catch (error) {
      showAlert("Error connecting to server", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleOutDate = async (id) => {
    try {
      const response = await axios.post(
        `${API_URL}updateOutDate`,
        { visitor_id: id },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        }
      );

      if (response.data.status === 1) {
        showAlert("Visitor successfully marked as gone! ✅", "success");
        fetchVisitors();
      } else {
        showAlert("Failed to update visitor status ❌", "error");
      }
    } catch (error) {
      showAlert("Server error occurred ⚠️", "error");
    }
  };

  useEffect(() => {
    fetchVisitors();
  }, []);

  const indexOfLastVisitor = currentPage * rowsPerPage;
  const indexOfFirstVisitor = indexOfLastVisitor - rowsPerPage;
  const currentVisitors = visitors.slice(
    indexOfFirstVisitor,
    indexOfLastVisitor
  );
  const totalPages = Math.ceil(visitors.length / rowsPerPage);

  const paginate = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  return (
    <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Alert Component */}
      {alert.show && (
        <div
          className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg transform transition-all duration-500 ${
            alert.type === "success"
              ? "bg-gradient-to-r from-green-500 to-green-600"
              : "bg-gradient-to-r from-red-500 to-red-600"
          } text-white font-medium text-lg flex items-center space-x-2`}
        >
          <span>{alert.message}</span>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-blue-600 p-6">
          <h2 className="text-3xl font-bold text-white">
            Visitor Management System
          </h2>
          <p className="text-green-100 mt-2">
            Track and manage visitor status
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-96">
            <div className="relative">
              <div className="w-12 h-12 border-4 border-blue-200 rounded-full animate-spin border-t-blue-600"></div>
              <div className="mt-4 text-gray-600 text-lg">Loading data...</div>
            </div>
          </div>
        ) : (
          <div className="p-6">
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-6 py-4 text-left text-base font-semibold text-gray-600 tracking-wider">
                      Visitor ID
                    </th>
                    <th className="px-6 py-4 text-left text-base font-semibold text-gray-600 tracking-wider">
                      Name
                    </th>
                    <th className="hidden md:table-cell px-6 py-4 text-left text-base font-semibold text-gray-600 tracking-wider">
                      Mobile
                    </th>
                    <th className="px-6 py-4 text-left text-base font-semibold text-gray-600 tracking-wider">
                      Time
                    </th>
                    <th className="px-6 py-4 text-right text-base font-semibold text-gray-600 tracking-wider">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {currentVisitors.map((visitor) => (
                    <tr
                      key={visitor.id}
                      className="hover:bg-green-50 transition-colors duration-200"
                    >
                      <td className="px-6 py-4 text-base text-gray-900 font-medium">
                        #{visitor.visitor_id}
                      </td>
                      <td className="px-6 py-4 text-base text-gray-800">
                        {visitor.visitor_name}
                      </td>
                      <td className="hidden md:table-cell px-6 py-4 text-base text-gray-800">
                        {visitor.mobile_number}
                      </td>
                      <td className="px-6 py-4">
                        {visitor.out_date ? (
                          <span className="text-base text-gray-800">
                            {visitor.out_date}
                          </span>
                        ) : (
                          <div className="space-y-1">
                            <div className="text-base text-gray-800 font-medium">
                              {new Date(
                                visitor.created_at
                              ).toLocaleDateString()}
                            </div>
                            <div className="text-sm text-green-600">
                              {new Date(
                                visitor.created_at
                              ).toLocaleTimeString()}
                            </div>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right">
                        {!visitor.out_date && (
                          <button
                            onClick={() => handleOutDate(visitor.visitor_id)}
                            className="inline-flex items-center px-4 py-2 rounded-lg text-white text-base font-medium transition-all duration-200 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                          >
                            <svg
                              className="h-5 w-5 mr-2"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                            <span className="hidden sm:inline">Mark Gone</span>
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="mt-6 flex items-center justify-between">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`relative inline-flex items-center px-4 py-2 text-sm font-medium rounded-md ${
                    currentPage === 1
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-300"
                  }`}
                >
                  Previous
                </button>
                <button
                  onClick={() => paginate(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`relative inline-flex items-center px-4 py-2 text-sm font-medium rounded-md ${
                    currentPage === totalPages
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-300"
                  }`}
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-base text-gray-700">
                    Showing{" "}
                    <span className="font-medium">
                      {indexOfFirstVisitor + 1}
                    </span>{" "}
                    to{" "}
                    <span className="font-medium">
                      {Math.min(indexOfLastVisitor, visitors.length)}
                    </span>{" "}
                    of <span className="font-medium">{visitors.length}</span>{" "}
                    results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    {Array.from({ length: totalPages }, (_, i) => (
                      <button
                        key={i + 1}
                        onClick={() => paginate(i + 1)}
                        className={`relative inline-flex items-center px-4 py-2 text-base font-medium ${
                          currentPage === i + 1
                            ? "z-10 bg-gradient-to-r from-green-600 to-blue-600 text-white"
                            : "bg-white text-gray-500 hover:bg-gray-50"
                        } border border-gray-300 ${
                          i === 0 ? "rounded-l-md" : ""
                        } ${i === totalPages - 1 ? "rounded-r-md" : ""}`}
                      >
                        {i + 1}
                      </button>
                    ))}
                  </nav>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VisitorsTable;
